/**
 * PromoPopup Component
 * 
 * Displays promotional popups/modals on page load.
 * Shows promos with display_type='popup'.
 * Each popup shows once per session (tracked in sessionStorage).
 */

import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { HiX, HiGift, HiTag, HiSparkles } from 'react-icons/hi';

interface Promo {
  id: string;
  title: string;
  description: string | null;
  discount_type: 'percentage' | 'fixed' | 'info' | null;
  discount_value: number | null;
  promo_code: string | null;
  free_item: string | null;
  banner_image_url: string | null;
  display_type: string;
  is_daily_special: boolean;
}

export default function PromoPopup() {
  const [promo, setPromo] = useState<Promo | null>(null);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    // Small delay before showing popup (better UX)
    const timer = setTimeout(() => {
      // Get already-seen popup IDs from session
      const seenIds = JSON.parse(sessionStorage.getItem('seen_promo_popups') || '[]');
      
      // Fetch active popup promos
      fetch('/api/promos/active?display_type=popup')
        .then((res) => res.json())
        .then((data) => {
          // Filter out already-seen popups
          const unseenPromos = (data.promos || []).filter(
            (p: Promo) => !seenIds.includes(p.id)
          );
          
          if (unseenPromos.length > 0) {
            // Show the highest priority unseen popup
            setPromo(unseenPromos[0]);
            setIsOpen(true);
            
            // Mark as seen
            seenIds.push(unseenPromos[0].id);
            sessionStorage.setItem('seen_promo_popups', JSON.stringify(seenIds));
          }
        })
        .catch((err) => {
          console.error('Failed to fetch promo popups:', err);
        });
    }, 1500); // 1.5 second delay

    return () => clearTimeout(timer);
  }, []);

  const handleClose = () => {
    setIsOpen(false);
  };

  // Copy promo code to clipboard
  const copyPromoCode = () => {
    if (promo?.promo_code) {
      navigator.clipboard.writeText(promo.promo_code);
      alert(`Code "${promo.promo_code}" copied to clipboard!`);
    }
  };

  if (!promo) return null;

  // Build discount display
  let discountDisplay = '';
  if (promo.discount_type === 'percentage' && promo.discount_value) {
    discountDisplay = `${promo.discount_value}% OFF`;
  } else if (promo.discount_type === 'fixed' && promo.discount_value) {
    discountDisplay = `$${promo.discount_value} OFF`;
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleClose}
            className="fixed inset-0 bg-black/50 z-50"
          />
          
          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none"
          >
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden pointer-events-auto">
              {/* Header with gradient */}
              <div className="relative bg-gradient-to-r from-purple-600 to-pink-500 p-6 text-white">
                {/* Close button */}
                <button
                  onClick={handleClose}
                  className="absolute top-3 right-3 p-1 hover:bg-white/20 rounded-full transition-colors"
                  aria-label="Close popup"
                >
                  <HiX className="w-6 h-6" />
                </button>
                
                {/* Icon */}
                <div className="flex justify-center mb-3">
                  {promo.is_daily_special ? (
                    <div className="bg-white/20 p-3 rounded-full">
                      <HiTag className="w-8 h-8" />
                    </div>
                  ) : promo.free_item ? (
                    <div className="bg-white/20 p-3 rounded-full">
                      <HiGift className="w-8 h-8" />
                    </div>
                  ) : (
                    <div className="bg-white/20 p-3 rounded-full">
                      <HiSparkles className="w-8 h-8" />
                    </div>
                  )}
                </div>
                
                {/* Daily special badge */}
                {promo.is_daily_special && (
                  <div className="text-center mb-2">
                    <span className="bg-yellow-400 text-yellow-900 px-3 py-1 rounded-full text-sm font-bold">
                      ⏰ TODAY ONLY!
                    </span>
                  </div>
                )}
                
                {/* Title */}
                <h2 className="text-2xl font-bold text-center">{promo.title}</h2>
                
                {/* Discount display */}
                {discountDisplay && (
                  <div className="text-center mt-2">
                    <span className="text-3xl font-extrabold">{discountDisplay}</span>
                  </div>
                )}
              </div>
              
              {/* Content */}
              <div className="p-6">
                {/* Banner image */}
                {promo.banner_image_url && (
                  <div className="mb-4 rounded-lg overflow-hidden">
                    <img
                      src={promo.banner_image_url}
                      alt={promo.title}
                      className="w-full h-40 object-cover"
                    />
                  </div>
                )}
                
                {/* Description */}
                {promo.description && (
                  <p className="text-gray-600 text-center mb-4">{promo.description}</p>
                )}
                
                {/* Free item */}
                {promo.free_item && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4 text-center">
                    <span className="text-green-700 font-medium">
                      🎁 Get a FREE {promo.free_item}!
                    </span>
                  </div>
                )}
                
                {/* Promo code */}
                {promo.promo_code && (
                  <div className="bg-gray-100 rounded-lg p-4 text-center mb-4">
                    <p className="text-sm text-gray-500 mb-2">Use this code at checkout:</p>
                    <button
                      onClick={copyPromoCode}
                      className="bg-purple-600 text-white px-6 py-2 rounded-lg font-mono font-bold text-lg hover:bg-purple-700 transition-colors"
                    >
                      {promo.promo_code}
                    </button>
                    <p className="text-xs text-gray-400 mt-2">Click to copy</p>
                  </div>
                )}
                
                {/* CTA button */}
                <button
                  onClick={handleClose}
                  className="w-full bg-gradient-to-r from-purple-600 to-pink-500 text-white py-3 rounded-lg font-semibold hover:from-purple-700 hover:to-pink-600 transition-all"
                >
                  {promo.promo_code ? 'Got it!' : 'Sweet!'}
                </button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
